import { useCallback, useRef, useState } from 'react';

import { exportApi } from '../../api/export.js';
import { referenceDataApi } from '../../api/referenceData.js';
import { useAuth } from '../../auth/AuthProvider.jsx';
import { Permission } from '../../auth/roles.js';
import { ErrorState, ExportCsvButton, LoadingState, MessageBanner } from '../../components/ui';
import { SortHeader, initialSortDirFor } from '../../components/ui/SortHeader.jsx';
import { useInfiniteScroll } from '../../hooks';
import { useUrlColumns, useUrlDebouncedParam, useUrlParam } from '../../url';
import { ColumnPicker } from './ColumnPicker.jsx';
import { HEADER_COLORS } from './constants.js';
import { formatValue } from './helpers.jsx';
import { SummaryRow } from './SummaryRow.jsx';

// Widths of the frozen right-hand columns; they stack by cumulative offset.
const FROZEN_NUM_WIDTH  = 80;
const FROZEN_BOOL_WIDTH = 110;

// Which row key carries the include/exclude flag, per toggleTable. One map
// rather than three inline ternaries — the Include column, the toggle write
// and the CSV all have to name the same key.
const INCLUDE_KEY = {
  sites:         'site_is_included',
  labor_metrics: 'lm_is_included',
  cost_centers:  'dept_is_included',
};

// Reusable explorer for the Sites / Labor Metrics / Cost Centers tabs.
// Behavior toggled by props:
//   - matchFilter:        adds Match dropdown (Sites only)
//   - toggleTable:        renders the include/exclude column for "sites",
//                         "labor_metrics", or "cost_centers"
//   - activeFilter:       adds NS-active dropdown (Sites + Cost Centers)
//   - editableNumberCol:  adds the editable threshold cell at the right
//   - editableBoolCols:   adds Yes/No switch cells to the right of it
//                         ({key, label, onSave, confirm, invert}) — Sites only
//                         today (OT recalc, Training / Drug Screening
//                         billability). `invert` = the column stores the
//                         negative of what the switch shows.
//   - urlActive:          this explorer is the visible Rules sub-tab, so its
//                         filters own the query string. Rules keeps every
//                         explorer mounted; without this flag the hidden ones
//                         would read the visible one's ?search= and refetch.
//   - exportDataset:      key in the backend's csv_export registry. The CSV is
//                         built from the same filters + the same chosen
//                         columns, over every matching row rather than the
//                         page loaded here.
export function DataExplorer({
  fetchData, idKey, idLabel, defaultCols,
  matchFilter, toggleTable, activeFilter, editableNumberCol,
  editableBoolCols = [], urlActive = true, exportDataset,
}) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(Permission.EDIT_REFERENCE_DATA);

  // Filters live in the URL so a link reproduces the exact table the sender
  // was looking at — same search, same narrowing, same columns, same sort.
  const urlOpts = { active: urlActive };
  const [selectedCols,  setSelectedCols]  = useUrlColumns('cols', defaultCols, urlOpts);
  const [pickerOpen,    setPickerOpen]    = useState(false);
  const [filterMatch,   setFilterMatch]   = useUrlParam('match', 'all', urlOpts);
  const [filterPunches, setFilterPunches] = useUrlParam('punches', 'all', urlOpts);
  const [filterStatus,  setFilterStatus]  = useUrlParam('status', 'included', urlOpts);
  const [filterActive,  setFilterActive]  = useUrlParam('ns_status', 'active', urlOpts);
  const [search, setSearch, debouncedSearch] = useUrlDebouncedParam('q', urlOpts);
  const [sortBy,  setSortBy]  = useUrlParam('sort_by', '', urlOpts);
  const [sortDir, setSortDir] = useUrlParam('sort_dir', 'asc', urlOpts);
  const [toggling,        setToggling]        = useState(null);
  const [savingThreshold, setSavingThreshold] = useState(null);
  const [savingFlag,      setSavingFlag]      = useState(null);
  const [msg, setMsg]   = useState(null);

  // The table scrolls inside its own box so the header row can stay frozen —
  // that box is also the infinite-scroll sentinel's root.
  const scrollRef = useRef(null);

  const handleSort = useCallback((col) => {
    if (sortBy === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortBy(col);
      setSortDir(initialSortDirFor(col));
    }
  }, [sortBy, sortDir, setSortBy, setSortDir]);

  // One filter builder for the table and the CSV, so an export is provably
  // the same narrowing the user is looking at. Paging is added by the caller.
  const queryParams = useCallback(() => {
    const params = { search: debouncedSearch, filter_punches: filterPunches };
    if (matchFilter)  params.filter_match  = filterMatch;
    if (toggleTable)  params.filter_status = filterStatus;
    if (activeFilter) params.filter_active = filterActive;
    if (sortBy) {
      params.sort_by  = sortBy;
      params.sort_dir = sortDir;
    }
    return params;
  }, [debouncedSearch, filterMatch, filterPunches, filterStatus,
      filterActive, matchFilter, toggleTable, activeFilter, sortBy, sortDir]);

  const fetchPage = useCallback(
    (offset, limit) => fetchData({ ...queryParams(), limit, offset }),
    [fetchData, queryParams],
  );

  const {
    items, summary, columns: allColumns, total,
    loading, loadingMore, hasMore, error, sentinelRef, updateItem,
  } = useInfiniteScroll(fetchPage, [fetchPage], { rootRef: scrollRef });

  const includeKey = INCLUDE_KEY[toggleTable];
  const getIncluded = (row) => (includeKey ? row[includeKey] : null);

  const handleToggle = async (row) => {
    if (!toggleTable) return;
    const code = row[idKey];
    const isIncluded = getIncluded(row);
    const newVal = isIncluded === false ? true : isIncluded === true ? false : false;
    const action = newVal ? 'Include' : 'Exclude';
    const entity =
      toggleTable === 'sites' ? 'site'
      : toggleTable === 'labor_metrics' ? 'labor metric'
      : 'cost center';
    if (!window.confirm(
      `${action} ${entity} "${code}" from billing? Excluded items are dropped from mapping output.`,
    )) return;
    setToggling(code);
    try {
      await referenceDataApi.toggleInclude(toggleTable, code, newVal);
      updateItem(idKey, code, { [includeKey]: newVal });
    } catch (e) {
      setMsg({ type: 'error', text: `Toggle failed: ${e.message}` });
    }
    setToggling(null);
  };

  const handleThresholdSave = async (row, newVal) => {
    if (!editableNumberCol) return;
    const code = row[idKey];
    const parsed = parseInt(newVal, 10);
    if (Number.isNaN(parsed)) return;
    if (parsed < (editableNumberCol.min || 1) || parsed > (editableNumberCol.max || 168)) return;
    if (parsed === row[editableNumberCol.key]) return;
    setSavingThreshold(code);
    try {
      await editableNumberCol.onSave(code, parsed);
      updateItem(idKey, code, { [editableNumberCol.key]: parsed });
    } catch (e) {
      setMsg({ type: 'error', text: `Save failed: ${e.message}` });
    }
    setSavingThreshold(null);
  };

  // A switch column reads Yes unless the row explicitly says otherwise, so a
  // NULL (site row written before the column existed) takes the default.
  // `invert` columns store the negative — df.org_units.ot_recalc_disabled = 1
  // is a switch showing No — so the whole cell reads and writes flipped.
  const isFlagOn = (row, col) => (col.invert
    ? !row[col.key]
    : row[col.key] !== false && row[col.key] !== 0);

  const handleFlagToggle = async (row, col) => {
    const code = row[idKey];
    const newVal = !isFlagOn(row, col);
    if (col.confirm && !window.confirm(col.confirm(code, newVal))) return;
    setSavingFlag(`${code}:${col.key}`);
    try {
      // onSave always takes the switch's own sense; only the cached row keeps
      // the stored sense, so an inverted column flips on the way back in.
      await col.onSave(code, newVal);
      updateItem(idKey, code, { [col.key]: col.invert ? !newVal : newVal });
    } catch (e) {
      setMsg({ type: 'error', text: `Save failed: ${e.message}` });
    }
    setSavingFlag(null);
  };

  // Frozen right-hand columns, left-to-right. Each is sticky at the cumulative
  // width of everything to its right, so they stack instead of overlapping.
  const frozenCols = [
    ...(editableNumberCol ? [{ ...editableNumberCol, kind: 'number', width: FROZEN_NUM_WIDTH }] : []),
    ...editableBoolCols.map((c) => ({ ...c, kind: 'bool', width: FROZEN_BOOL_WIDTH })),
  ];
  const frozenRight = frozenCols.map(
    (_, i) => frozenCols.slice(i + 1).reduce((sum, c) => sum + c.width, 0),
  );

  const toggleCol = (key) => {
    const next = new Set(selectedCols);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelectedCols(next);
  };
  const selectTable = (table) => {
    const tableKeys = allColumns.filter((c) => c.table === table).map((c) => c.key);
    const allSelected = tableKeys.every((k) => selectedCols.has(k));
    const next = new Set(selectedCols);
    tableKeys.forEach((k) => (allSelected ? next.delete(k) : next.add(k)));
    setSelectedCols(next);
  };

  const activeCols = allColumns.filter((c) => selectedCols.has(c.key));

  // The CSV is the table: the same columns, left to right, including the
  // Include cell and the frozen editable ones on the right. Sixty columns are
  // available here and the user chose these — exporting all sixty would be a
  // different table from the one they built.
  const exportColumns = [
    ...(toggleTable ? [includeKey] : []),
    idKey,
    ...activeCols.map((c) => c.key),
    ...frozenCols.map((c) => c.key),
  ];

  return (
    <>
      <SummaryRow summary={summary} />

      <div style={{ marginBottom: 12 }}>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => setPickerOpen(!pickerOpen)}
          style={{ marginBottom: 8 }}
        >
          {pickerOpen ? 'Hide Columns' : 'Choose Columns'} ({activeCols.length} selected)
        </button>
        {pickerOpen && (
          <ColumnPicker
            columns={allColumns}
            selectedCols={selectedCols}
            onToggle={toggleCol}
            onSelectTable={selectTable}
          />
        )}
      </div>

      <div style={{
        display: 'flex', gap: 12, marginBottom: 16,
        flexWrap: 'wrap', alignItems: 'center',
      }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Search</label>
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..." style={{ width: 200 }}
          />
        </div>
        {matchFilter && (
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: 11 }}>Match</label>
            <select value={filterMatch} onChange={(e) => setFilterMatch(e.target.value)}>
              <option value="all">All</option>
              <option value="matched">Matched (DF+NS)</option>
              <option value="unmatched">Unmatched (DF+NS)</option>
              <option value="df_only">Dayforce Only</option>
              <option value="ns_only">NetSuite Only</option>
            </select>
          </div>
        )}
        {toggleTable && (
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: 11 }}>Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All</option>
              <option value="included">Included</option>
              <option value="excluded">Excluded</option>
            </select>
          </div>
        )}
        {activeFilter && (
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: 11 }}>NS Status</label>
            <select value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
              <option value="active">Active in NetSuite</option>
              <option value="all">All</option>
              <option value="inactive">Inactive in NetSuite</option>
            </select>
          </div>
        )}
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Punches</label>
          <select value={filterPunches} onChange={(e) => setFilterPunches(e.target.value)}>
            <option value="all">All</option>
            <option value="with">With Punches</option>
            <option value="without">No Punches</option>
          </select>
        </div>
        {exportDataset && (
          <ExportCsvButton
            onExport={() => exportApi.download(exportDataset, {
              params: queryParams(), columns: exportColumns,
            })}
            disabled={loading || total === 0 || activeCols.length === 0}
            title="Download every row matching these filters as CSV, with the columns shown here — not just the rows loaded"
            onError={(text) => setMsg({ type: 'error', text })}
            style={{ alignSelf: 'flex-end', paddingBottom: 2 }}
          />
        )}
        <div style={{ fontSize: 12, color: '#666', alignSelf: 'flex-end', paddingBottom: 4 }}>
          Showing {items.length} of {total}
        </div>
      </div>

      <MessageBanner msg={msg} onDismiss={() => setMsg(null)} />
      {error && <ErrorState>{error}</ErrorState>}

      {loading
        ? <LoadingState />
        : activeCols.length === 0
          ? <div className="alert alert-info">
              No columns selected. Click "Choose Columns" to pick which data to display.
            </div>
          : (
            <div className="table-scroll" ref={scrollRef}>
              <table>
                <thead>
                  <tr>
                    {toggleTable && <th style={{ width: 70, textAlign: 'center' }}>Include</th>}
                    <SortHeader col={idKey} sortBy={sortBy} sortDir={sortDir} onClick={handleSort}>
                      {idLabel}
                    </SortHeader>
                    {activeCols.map((col) => (
                      <SortHeader
                        key={col.key} col={col.key}
                        sortBy={sortBy} sortDir={sortDir} onClick={handleSort}
                        style={{ fontSize: 11, whiteSpace: 'nowrap' }}
                      >
                        <span style={{ display: 'block', fontSize: 9, fontWeight: 400, color: HEADER_COLORS[col.group] || '#ccc' }}>
                          {col.table || col.group}
                        </span>
                        {col.label}
                      </SortHeader>
                    ))}
                    {frozenCols.map((col, i) => (
                      <SortHeader
                        key={col.key} col={col.key}
                        sortBy={sortBy} sortDir={sortDir} onClick={handleSort}
                        style={{
                          width: col.width, textAlign: 'center',
                          // z-4: above the frozen header row (z-3) it slides under.
                          position: 'sticky', right: frozenRight[i],
                          background: '#002159', zIndex: 4,
                          // Only the leftmost frozen column draws the divider.
                          ...(i === 0 ? { boxShadow: '-2px 0 4px rgba(0,0,0,0.1)' } : {}),
                        }}
                      >
                        {col.label}
                      </SortHeader>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r[idKey]} style={{ opacity: toggleTable && getIncluded(r) === false ? 0.4 : 1 }}>
                      {toggleTable && (
                        <td style={{ textAlign: 'center', padding: '4px 8px' }}>
                          {canEdit ? (
                            <button
                              onClick={() => handleToggle(r)}
                              disabled={toggling === r[idKey]}
                              style={{
                                width: 50, padding: '3px 0', fontSize: 11, fontWeight: 600,
                                borderRadius: 12, border: 'none', cursor: 'pointer',
                                background: getIncluded(r) === false ? '#f8d7da' : '#d4edda',
                                color: getIncluded(r) === false ? '#721c24' : '#155724',
                              }}
                            >
                              {getIncluded(r) === false ? 'Excl' : 'Incl'}
                            </button>
                          ) : (
                            <span style={{ fontSize: 11, color: '#666' }}>
                              {getIncluded(r) === false ? 'Excl' : 'Incl'}
                            </span>
                          )}
                        </td>
                      )}
                      <td><strong>{r[idKey]}</strong></td>
                      {activeCols.map((col) => (
                        <td key={col.key} style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                          {formatValue(r[col.key], col.key)}
                        </td>
                      ))}
                      {frozenCols.map((col, i) => (
                        <td key={col.key} style={{
                          textAlign: 'center', padding: '4px 8px',
                          position: 'sticky', right: frozenRight[i],
                          background: '#fff', zIndex: 1,
                          ...(i === 0 ? { boxShadow: '-2px 0 4px rgba(0,0,0,0.06)' } : {}),
                        }}>
                          {/* NS-only placeholder rows have no df.org_units row —
                              nothing to edit on either kind of frozen column. */}
                          {r.in_dayforce === false ? (
                            <span style={{ color: '#ccc' }}>—</span>
                          ) : col.kind === 'number' ? (
                            canEdit ? (
                              <input
                                type="number"
                                defaultValue={r[col.key] ?? 40}
                                min={col.min || 1}
                                max={col.max || 168}
                                disabled={savingThreshold === r[idKey]}
                                onBlur={(e) => handleThresholdSave(r, e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                style={{
                                  width: 52, padding: '3px 4px', fontSize: 12,
                                  textAlign: 'center', border: '1px solid #ccc', borderRadius: 4,
                                }}
                              />
                            ) : (
                              <span style={{ fontSize: 12, color: '#666' }}>
                                {r[col.key] ?? 40}
                              </span>
                            )
                          ) : canEdit ? (
                            <button
                              onClick={() => handleFlagToggle(r, col)}
                              disabled={savingFlag === `${r[idKey]}:${col.key}`}
                              style={{
                                width: 50, padding: '3px 0', fontSize: 11, fontWeight: 600,
                                borderRadius: 12, border: 'none', cursor: 'pointer',
                                background: isFlagOn(r, col) ? '#d4edda' : '#f8d7da',
                                color:      isFlagOn(r, col) ? '#155724' : '#721c24',
                              }}
                            >
                              {isFlagOn(r, col) ? 'Yes' : 'No'}
                            </button>
                          ) : (
                            <span style={{ fontSize: 11, color: '#666' }}>
                              {isFlagOn(r, col) ? 'Yes' : 'No'}
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div ref={sentinelRef} style={{ height: 1 }} />
              {loadingMore && (
                <div style={{ textAlign: 'center', padding: 12, color: '#666', fontSize: 13 }}>
                  Loading more...
                </div>
              )}
              {!hasMore && items.length > 0 && (
                <div style={{ textAlign: 'center', padding: 12, color: '#999', fontSize: 12 }}>
                  All {total} rows loaded
                </div>
              )}
            </div>
          )
      }
    </>
  );
}
